/**
 * Reading credit off an order WITHOUT charging for it twice.
 *
 * The one thing worth knowing about customer credit on the front end:
 *
 *     order.amount is ALREADY NET OF CREDIT.
 *
 * `OrderService.calculateTotalAmount` ends with
 * `order.amount = preCreditTotalCents - creditCents`, and the DTO says the same
 * in prose: "`amount` is what is still owed, so the pre-credit total the
 * customer sees is `amount + creditAppliedCents`". Credit is payment, not a
 * discount on the goods, and it is settled after any coupon.
 *
 * So the UI subtracts NOTHING. A second subtraction in `delivery.tsx`'s total
 * would quote a price below what Stripe then charges — the customer commits to
 * one number and is billed a higher one, which is the worst direction for this
 * class of bug to fail in. `payableCents` exists to be the identity and to have
 * a test that says so, so that nobody "fixes" the total later.
 *
 * The reconstruction only ever goes the other way: `preCreditTotalCents` adds
 * the credit back to show what the order would have cost.
 */

/** The only two fields any of this looks at. */
type CreditBearingOrder = {
  amount?: number | string | null;
  /** Shipping actually charged on the order, as the server priced it. */
  shippingPrice?: number | string | null;
  /**
   * Documented as required with a meaningful 0 — but an order cached before
   * the field existed has it undefined, and TypeORM hands `int` columns back
   * as strings often enough that a bare `+` would concatenate. Both are
   * normalised here rather than at every call site.
   */
  creditAppliedCents?: number | string | null;
};

function cents(value: number | string | null | undefined): number {
  const parsed = Math.round(Number(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Credit already redeemed against this order, in minor units.
 *
 * Clamped at 0: a negative redemption is not a thing, and letting one through
 * would silently INFLATE the pre-credit total it is added to.
 */
export function appliedCreditCents(
  order: CreditBearingOrder | null | undefined,
): number {
  return Math.max(0, cents(order?.creditAppliedCents));
}

/**
 * Presence, not truthiness — the distinction the backend calls out explicitly.
 * An order whose credit was removed reads 0 and must stop showing the chip.
 */
export function hasCreditApplied(
  order: CreditBearingOrder | null | undefined,
): boolean {
  return appliedCreditCents(order) > 0;
}

/**
 * What the customer still has to pay. Deliberately the server's number,
 * untouched. See the file comment.
 */
export function payableCents(
  order: CreditBearingOrder | null | undefined,
): number {
  return cents(order?.amount);
}

/** What the order would have cost with no credit on it. */
export function preCreditTotalCents(
  order: CreditBearingOrder | null | undefined,
): number {
  return payableCents(order) + appliedCreditCents(order);
}

/**
 * What the customer is BUYING, before credit and without shipping.
 *
 * THE RULE, because this is the one thing here that is easy to get backwards:
 *
 *     each threshold measures the thing it is actually about.
 *
 * FREE SHIPPING is about how much the customer BOUGHT. It is a reward for
 * basket size, and credit is a payment method, not a smaller basket — so it
 * measures goods, PRE-credit, and that is this function. Same for a coupon's
 * `minimumAmount`: the live check is `subtotal < coupon.minimumAmount` in
 * `calculateDiscountsForOrder` (backend `src/utils/pricing/price-calculator.ts`),
 * where `subtotal` is the sum of the line-item prices — goods only, with no
 * credit anywhere near it. Credit is subtracted afterwards, at the end of
 * `OrderService.calculateTotalAmount`
 * (`order.amount = preCreditTotalCents - creditCents`), so a coupon minimum
 * is a pre-credit question too.
 *
 * (`CouponService.calculateDiscountedPrice` also holds a `minimumAmount`
 * check and looks like the authority. It has no live callers — the only ones
 * are in `commented-out.ts` — so citing it would have described a path that
 * does not run.)
 *
 * CASH ON DELIVERY is about how much cash the COURIER PHYSICALLY COLLECTS. If
 * credit covers most of the order the courier collects almost nothing and the
 * entire reason for the floor is gone — so that one measures the payable
 * amount, POST-credit. It is `payableGoodsCents`, below, and mixing the two up
 * is how the delivery page came to offer COD on orders the server refuses.
 *
 * Both were written as `order.amount - order.shippingPrice`, which was one
 * expression for both questions while `amount` was the whole price. Credit is
 * what split them: `amount` is now net of credit, so the old expression
 * silently became the post-credit basis everywhere, which is right for COD and
 * wrong for free shipping.
 *
 * Left unfixed on the free-shipping side, EUR 10 of credit on a EUR 40 cart
 * reads as under a EUR 35 threshold, and the delivery page adds a shipping
 * charge the server is not charging: the screen where the customer decides
 * whether to pay shows a total higher than the one they will be billed.
 */
export function goodsCents(
  order: CreditBearingOrder | null | undefined,
): number {
  return Math.max(
    0,
    preCreditTotalCents(order) - cents(order?.shippingPrice),
  );
}

/**
 * What the courier actually collects: the payable total, less shipping.
 *
 * The basis for the cash-on-delivery minimum, and ONLY for that. It is
 * `order.amount - order.shippingPrice` — post-credit — named rather than
 * inlined precisely so that the next reader who sees a bare
 * `amount - shippingPrice` beside `goodsCents` does not "unify" the two.
 *
 * This matches `OrderService.assertCodAllowed`, which computes
 * `Math.round(Number(order.amount)) - Math.round(Number(order.shippingPrice))`
 * — post-credit, because `order.amount` is. Matching it is not a nicety: that
 * method's doc comment requires that "the server must never be stricter than
 * the most permissive client", because being more permissive IN THE CLIENT is
 * a wall the customer hits at the last step, on an option the page itself
 * offered them. A pre-credit basis here means the page invites COD on carts
 * the server then refuses with COD_BELOW_MINIMUM.
 *
 * (`applyCreditToOrder` also computes a goods figure, pre-credit, and its
 * comment says it is "the measure assertCodAllowed uses". That refers to the
 * SHAPE of the expression — total minus shipping actually charged — not to the
 * basis. Reading it as the basis is what put COD on the wrong side of this
 * file once already.)
 *
 * Every reference above names a SYMBOL, not a line number, and deliberately:
 * line numbers in the backend rot within a commit or two, and a rotted
 * citation is worse than none because it reads as verified. A symbol can be
 * grepped — and grepping it is also how you check that the path you are
 * describing is one that actually runs, which is the step that was skipped
 * both times this file's comments named the wrong authority.
 */
export function payableGoodsCents(
  order: CreditBearingOrder | null | undefined,
): number {
  return Math.max(0, payableCents(order) - cents(order?.shippingPrice));
}

/**
 * The live total on the delivery step, in minor units, while the customer is
 * still choosing shipping and payment: the server's `amount`, with the
 * shipping and cash-on-delivery fee it priced swapped for the ones now
 * selected.
 *
 * Floored at 0, as the photo-options total is. `amount` is net of credit, so
 * with credit covering most of the order, picking a cheaper shipping than the
 * one on the order took the figure below zero and the price bar showed a
 * negative total. The server clamps the same way; nothing is ever owed back.
 */
export function deliveryStepTotalCents(args: {
  amountCents: number;
  /** Shipping for the method now selected; undefined when not priced. */
  selectedShippingCents?: number;
  /** Shipping already on the order. */
  orderShippingCents?: number | null;
  /** Cash-on-delivery fee, charged when COD is selected. */
  codFeeCents: number;
  selectingCod: boolean;
  /** The order already carries COD, so its fee is inside `amountCents`. */
  orderIsCod: boolean;
}): number {
  let total = args.amountCents;
  if (args.selectedShippingCents !== undefined) total += args.selectedShippingCents;
  if (args.orderShippingCents) total -= args.orderShippingCents;
  if (args.selectingCod) total += args.codFeeCents;
  if (args.orderIsCod) total -= args.codFeeCents;
  return Math.max(0, total);
}

/**
 * The PRE-credit total of the delivery step as the customer has it selected
 * right now: what the order would cost with the shipping and payment on
 * screen, before credit. `preCreditCents` is the server's
 * (`preCreditTotalCents(order)`), priced for the shipping on the ORDER; the
 * selected shipping and COD fee are swapped in the same way
 * `deliveryStepTotalCents` swaps them.
 */
export function deliveryStepPreCreditCents(args: {
  preCreditCents: number;
  selectedShippingCents?: number;
  orderShippingCents?: number | null;
  codFeeCents: number;
  selectingCod: boolean;
  orderIsCod: boolean;
}): number {
  let total = args.preCreditCents;
  if (args.selectedShippingCents !== undefined) total += args.selectedShippingCents;
  if (args.orderShippingCents) total -= args.orderShippingCents;
  if (args.selectingCod) total += args.codFeeCents;
  if (args.orderIsCod) total -= args.codFeeCents;
  return Math.max(0, total);
}

/**
 * The credit that will actually pay the order as the step now stands.
 *
 * Credit was applied for the shipping on the order. Switching to a cheaper
 * one (store pickup) leaves more on the order than it now costs, and the
 * server releases the excess when the order is submitted (stage walk B7:
 * 6,96 € on a 3,00 € order took 3,00 € and released 3,96 €). Until then the
 * screen must already say 3,00 €, so the credit lines, the "used" total and
 * the price bar all add up: `stepPreCredit − this` is exactly
 * `deliveryStepTotalCents` for the same selection.
 *
 * `stepPreCreditCents` undefined: the selection is not known, so the order's
 * own figure stands.
 */
export function deliveryStepCreditCents(
  appliedCents: number,
  stepPreCreditCents: number | null | undefined,
): number {
  const applied = Math.max(0, appliedCents);
  if (stepPreCreditCents === null || stepPreCreditCents === undefined) return applied;
  return Math.min(applied, Math.max(0, stepPreCreditCents));
}
